export interface Experiment {
    id: string
    name: string
    description: string
    batchId: string
    experimentType: string
    subjectType: string
    status: string
    startDate: string
    endDate?: string
    treatmentEndDate?: string
    targetAudience: string
    canvasName?: string
    metrics: string[]
    variants: ExperimentVariant[]
    createdAt: string
    createdBy: string
    teamOwner: string
    platforms: string[]
    minAppVersions: {
        iOS?: string
        Android?: string
    }
    labels?: {
        platforms?: string
        [key: string]: string | undefined
    }
    markets: {
        type: 'all' | 'zones'
        zones?: number[]
    }
    rolloutType: 'launch' | 'rollout'
    outcome?: string
    outcomeDetails?: string
    testSalt?: string
    flags?: string[]
}

export interface ExperimentVariant {
    id: string
    name: string
    description: string
    trafficAllocation: number
    configuration: Record<string, any>
    isControl?: boolean
    startRange?: number
    endRange?: number
    percentage?: number
}

// API contracts and converters
export interface ApiMetric {
    id: string
    name: string
    type: string
}

export interface ApiExperiment {
    id: string
    name: string
    description: string
    batch_id: string
    owner: string
    start_date: string
    end_date: string
    treatment_end_date?: string
    subject_type: string
    lifecycle_state: string
    outcome?: string
    test_salt: string
    test_groups: {
        id: string
        name: string
        description: string
        start_range: number
        end_range: number
        is_control: boolean
    }[]
    flags: string[]
    labels: {
        audience: string
        app_platforms?: string
        platforms?: string
        [key: string]: string | undefined
    }
    zones?: number[]
    metrics?: ApiMetric[]
    rollout_salt?: string
    rollout_groups?: {
        id: string
        name: string
        start_range: number
        end_range: number
        start_date: string
        end_date: string
    }[]
    metadata?: {
        android_app_version?: string
        ios_app_version?: string
        [key: string]: any
    }
}

const calculateRanges = (variants: any[]) => {
    const totalRange = 1000
    let currentStart = 0
    return variants.map(variant => {
        const percentage = variant.trafficAllocation || 0
        const rangeSize = Math.round((percentage / 100) * totalRange)
        const startRange = currentStart
        const endRange = Math.min(currentStart + rangeSize - 1, totalRange - 1)
        currentStart = endRange + 1
        return { ...variant, startRange, endRange }
    })
}

const parseMarkets = (zones?: number[]) => {
    if (!zones || zones.length === 0) {
        return { type: 'all' as const }
    }
    return { type: 'zones' as const, zones }
}

const convertMarketsToZones = (markets: any) => {
    if (markets.type === 'all') {
        return []
    }
    if (markets.type === 'zones' && markets.zones) {
        return markets.zones
    }
    return []
}

export function convertExperimentToApiFormat(experiment: Experiment): ApiExperiment {
    const platformLabels = experiment.platforms.join(',')
    const zones = convertMarketsToZones(experiment.markets)
    const metricMap: Record<string, ApiMetric> = {
        rides: { id: 'rides', name: 'Rides', type: 'Continuous' },
        rider_conversion: { id: 'rider_conversion', name: 'Rider Conversion', type: 'Ratio' },
        revenue: { id: 'revenue', name: 'Revenue', type: 'Continuous' },
        vehicle_profit: { id: 'vehicle_profit', name: 'Vehicle Profit', type: 'Continuous' },
        payg_revenue: { id: 'payg_revenue', name: 'PAYG Revenue', type: 'Continuous' },
        subscription_revenue: { id: 'subscription_revenue', name: 'Subscription Revenue', type: 'Continuous' },
        purchase_revenue: { id: 'purchase_revenue', name: 'Purchase Revenue', type: 'Continuous' },
        subscription_pass_conversion: {
            id: 'subscription_pass_conversion',
            name: 'Subscription Pass Conversion',
            type: 'Ratio',
        },
        purchase_pass_conversion: { id: 'purchase_pass_conversion', name: 'Purchase Pass Conversion', type: 'Ratio' },
        no_parking_photo: { id: 'no_parking_photo', name: 'No Parking Photo', type: 'Continuous' },
        bad_photo: { id: 'bad_photo', name: 'Bad Photo', type: 'Continuous' },
        good_parking: { id: 'good_parking', name: 'Good Parking', type: 'Continuous' },
    }
    const apiMetrics = experiment.metrics.map(id => metricMap[id]).filter(Boolean)
    const updatedLabels = { audience: experiment.targetAudience, ...experiment.labels, platforms: platformLabels }
    const metadata: any = {}
    if (experiment.minAppVersions?.iOS) {
        metadata.ios_app_version = experiment.minAppVersions.iOS
    }
    if (experiment.minAppVersions?.Android) {
        metadata.android_app_version = experiment.minAppVersions.Android
    }
    if (experiment.targetAudience === 'Canvas entry' && experiment.canvasName) {
        metadata.canvas_name = experiment.canvasName
    }

    const apiExperiment: ApiExperiment = {
        id: experiment.id,
        name: experiment.name,
        description: experiment.description,
        batch_id: experiment.batchId,
        owner: experiment.teamOwner,
        start_date: experiment.startDate,
        end_date: experiment.endDate || '',
        subject_type: experiment.subjectType,
        lifecycle_state: experiment.status,
        test_salt: experiment.testSalt || '',
        test_groups: calculateRanges(experiment.variants).map(variant => ({
            id: variant.id,
            name: variant.name,
            description: variant.description,
            start_range: variant.startRange!,
            end_range: variant.endRange!,
            is_control: variant.isControl || false,
        })),
        flags: experiment.flags || [],
        labels: updatedLabels,
        zones: zones.length > 0 ? zones : undefined,
        metrics: apiMetrics.length > 0 ? apiMetrics : undefined,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    }

    if (experiment.treatmentEndDate) {
        apiExperiment.treatment_end_date = experiment.treatmentEndDate
    }
    if (experiment.outcome) {
        apiExperiment.outcome = experiment.outcome
    }

    return apiExperiment
}

export function convertApiExperimentToExperiment(apiExp: ApiExperiment): Experiment {
    const platformString = apiExp.labels?.app_platforms || apiExp.labels?.platforms || ''
    const platforms = platformString
        ? platformString
              .split(',')
              .map(p => p.trim())
              .filter(p => p.length > 0)
        : ['iOS', 'Android']
    const markets = parseMarkets(apiExp.zones)
    const metrics = (apiExp.metrics || []).map(m => m.id)
    let status: 'draft' | 'scheduled' | 'active' | 'ended' = 'draft'
    const statusField = apiExp.lifecycle_state || (apiExp as any).status
    if (statusField) {
        switch (statusField.toLowerCase()) {
            case 'active':
            case 'running':
            case 'live':
                status = 'active'
                break
            case 'ended':
            case 'completed':
            case 'finished':
            case 'stopped':
                status = 'ended'
                break
            case 'ready':
                status = 'scheduled'
                break
            case 'draft':
            case 'pending':
            case 'scheduled':
            case 'paused':
            default:
                status = 'draft'
        }
    } else {
        const now = new Date()
        const startDate = new Date(apiExp.start_date)
        const endDate = new Date(apiExp.end_date)
        if (now < startDate) {
            status = 'draft'
        } else if (now > endDate) {
            status = 'ended'
        } else {
            status = 'active'
        }
    }

    const variants = apiExp.test_groups.map(group => {
        const rangeSize = group.end_range - group.start_range
        const percentage = Math.round((rangeSize / 1000) * 100)
        return {
            id: group.id,
            name: group.name,
            description: group.description || '',
            trafficAllocation: percentage,
            configuration: {},
            startRange: group.start_range,
            endRange: group.end_range,
            percentage,
            isControl: group.is_control,
        }
    })

    return {
        id: apiExp.id,
        name: apiExp.name,
        description: apiExp.description || '',
        batchId: apiExp.batch_id,
        experimentType: 'user',
        subjectType: apiExp.subject_type,
        status,
        startDate: apiExp.start_date,
        endDate: apiExp.end_date,
        treatmentEndDate: apiExp.treatment_end_date,
        targetAudience: apiExp.labels?.audience || '',
        metrics,
        variants,
        createdAt: apiExp.start_date,
        createdBy: apiExp.owner,
        teamOwner: apiExp.owner,
        platforms,
        minAppVersions: {
            iOS: apiExp.metadata?.ios_app_version,
            Android: apiExp.metadata?.android_app_version,
        },
        labels: apiExp.labels || {},
        markets,
        rolloutType: 'launch',
        outcome: apiExp.outcome,
        testSalt: apiExp.test_salt || '',
        flags: apiExp.flags || [],
    }
}

export const getSubjectTypeDisplayName = (subjectType: string): string => {
    switch (subjectType) {
        case 'account_id':
            return 'Rider App User'
        case 'vehicle_id':
            return 'vehicle'
        case 'device_id':
            return 'device'
        default:
            return subjectType
    }
}
